<template>
  <div class="mb-3">
    <div
      class="d-flex align-items-center"
      :class="{
        'mb-3':
          searchable || filterable || $slots.beforeSearch || $slots.afterSearch,
      }"
    >
      <slot name="beforeSearch" />
      <b-form-group v-if="searchable" class="mb-0" label-for="filter">
        <b-input-group class="input-group-labeled">
          <b-form-input
            v-model="searchVal"
            class="pr-0"
            placeholder="Vyhľadať..."
            @keydown.enter="search"
          />
          <b-input-group-append>
            <b-btn variant="link" class="px-2" @click="search">
              <b-icon icon="search" />
            </b-btn>
            <b-btn v-if="searchVal" variant="link" class="px-2" @click="reset">
              <b-icon icon="x-circle" />
            </b-btn>
            <b-btn
              v-if="filterable"
              v-b-toggle.toggleFilter
              v-b-tooltip.hover.bottom
              class="p-1"
              :variant="showFilter ? 'primary' : 'link'"
              title="Filter"
            >
              <b-icon icon="filter" />
              <b-icon :icon="`arrow-${showFilter ? 'up' : 'down'}-short`" />
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <slot name="afterSearch" />
    </div>
    <b-collapse
      id="toggleFilter"
      @show="
        showFilter = true
        $emit('showFilter')
      "
      @hide="
        showFilter = false
        $emit('hideFilter')
      "
    >
      <div class="bg-white pt-2 px-3">
        <slot name="filter" />
      </div>
    </b-collapse>
    <b-alert
      :variant="selectedTotal ? 'primary' : 'white'"
      :show="!!(selectedAll && selected.length)"
      class="mb-0"
    >
      <template v-if="!selectedTotal">
        Všetky položky <strong>({{ selected.length }})</strong> na tejto stránke
        sú vybraté
        <b-btn
          v-if="canSelectTotal"
          size="sm"
          class="ml-1"
          @click="selectTotal(true)"
        >
          Vybrať všetky <strong>({{ totalRows }})</strong>
        </b-btn>
      </template>
      <template v-else>
        Všetky položky <strong>({{ totalRows }})</strong> sú vybraté
        <b-btn size="sm" class="ml-1" @click="selectTotal(false)">
          Resetovať výber
        </b-btn>
      </template>
    </b-alert>
    <div class="table-container" :class="{ loading: isBusy }">
      <b-table
        ref="table"
        :busy.sync="isBusy"
        :class="tableClass"
        :fields="columns"
        :items="dataProvider"
        :per-page="0"
        :sort-desc="sortDesc"
        class="bg-white"
        show-empty
        :empty-text="
          isBusy ? 'Načítavanie...' : 'Neboli nájdené žiadne záznamy'
        "
        v-bind="$attrs"
        v-on="$listeners"
        @refreshed="refreshed"
      >
        <template v-if="selectable" #head(select)="">
          <b-form-checkbox
            v-if="data.rows && data.rows.length"
            v-model="selectedAll"
            :indeterminate="indSelectAll"
            @change="selectAll"
          />
        </template>
        <template #cell(select)="data">
          <b-form-checkbox
            v-model="selected"
            :value="data.item[selectTrackBy]"
          />
        </template>
        <template v-for="(_, slot) of $scopedSlots" #[slot]="scope">
          <slot v-bind="scope" :name="slot" />
        </template>
      </b-table>
      <div v-if="isBusy" class="loading">
        <div class="spinner-border" />
      </div>
    </div>
    <div class="d-flex align-items-center">
      <div class="d-flex align-items-center">
        <div class="mr-2">Celkom: {{ totalRows }} záznamov</div>
        <div v-if="selectable && data.rows" class="mr-2">
          Označené: {{ selected.length }}/{{ data.rows.length }}
        </div>
        <slot name="selectActions" :selected="selected" />
      </div>
      <div class="d-flex ml-auto">
        <b-form-select
          v-model="perPage"
          size="sm"
          :current-page="currentPage"
          :options="pageOptions"
          class="mr-2"
        />
        <b-pagination
          v-model="currentPage"
          size="sm"
          :per-page="perPage"
          :total-rows="totalRows"
          class="my-0"
        />
      </div>
    </div>
  </div>
</template>
<script>
export default {
  inheritAttrs: false,
  props: {
    tableClass: {
      type: String,
      default: '',
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectTrackBy: {
      type: String,
      default: 'id',
    },
    canSelectTotal: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      required: true,
    },
    apiRequest: {
      type: Function,
      required: true,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    pageOptions: {
      type: Array,
      default() {
        return [10, 20, 50, 100]
      },
    },
    sortDesc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBusy: false,
      searchVal: null,
      searchIn: [],
      showFilter: false,
      totalRows: 0,
      currentPage: 1,
      data: {},
      selectedAll: false,
      selectedTotal: false,
      indSelectAll: false,
      selected: [],
      selectedRows: [],
    }
  },
  watch: {
    currentPage() {
      this.refresh()
    },
    perPage() {
      this.refresh()
    },
    selected(items) {
      this.selectedTotal = false
      if (items.length && items.length < this.data.rows?.length) {
        this.selectedAll = false
        this.indSelectAll = true
      } else if (items.length === this.data.rows?.length) {
        this.selectedAll = true
        this.indSelectAll = false
      } else {
        this.selectedAll = false
        this.indSelectAll = false
      }

      this.selectedRows = this.data.rows
        ? this.data.rows.filter((row) =>
            items.includes(row[this.selectTrackBy]),
          )
        : []
      this.$emit('selectedItems', items)
    },
    selectedRows(items) {
      this.$emit('selectedRows', items)
    },
    selectedTotal(val) {
      this.$emit('selectedTotal', val)
    },
  },
  mounted() {
    if (this.selectable && this.columns) {
      this.columns.unshift({
        key: 'select',
        label: '',
        thClass: 'th-shrink',
      })
    }
  },
  methods: {
    dataProvider(ctx, callback) {
      this.apiRequest(this.getRequestParams())
        .then((res) => {
          this.data = res.data
          this.totalRows = res.data.total
          callback(res.data.rows)
        })
        .catch(() => {
          callback([])
        })

      return null
    },
    getData(item) {
      return item ? this.data[item] : this.data
    },
    getRequestParams() {
      let params = {}

      for (let column of this.columns) {
        if (column.searchable && !this.searchIn.includes(column.sortableKey)) {
          this.searchIn.push(column.sortableKey)
        }
      }

      let table = this.$refs.table

      params.query = this.searchVal

      if (this.searchIn.length) {
        params.searchIn = this.searchIn
      }

      params.startAt = (this.currentPage - 1) * this.perPage
      params.maxResults = this.perPage

      if (table.localSortBy) {
        let sortBy = {}
        sortBy = this.columns.find((e) => e.key === table.localSortBy)

        let orderBy = {}
        orderBy[sortBy.sortableKey ? sortBy.sortableKey : table.localSortBy] =
          table.localSortDesc ? 'desc' : 'asc'
        params.orderBy = orderBy
      }
      return params
    },
    refresh() {
      this.$refs.table.refresh()
    },
    refreshed() {
      this.$emit('refreshed')
      this.selected = []
    },
    search() {
      this.refresh()
    },
    reset() {
      this.searchVal = null
      this.search()
    },
    applyFilter() {
      this.$emit('filterApply')
      this.search()
    },
    resetFilter() {
      this.$emit('filterReset')
      this.showFilter = false
    },
    selectAll(checked) {
      this.selected = checked
        ? this.data.rows.map((a) => a[this.selectTrackBy])
        : []
      this.selectedRows = checked ? this.data.rows.map((a) => a) : []
    },
    selectTotal(val) {
      this.selectedTotal = val
      if (!val) {
        this.selectedAll = false
        this.selected = []
      }
    },
  },
}
</script>
